import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  LayoutDashboard, Users, CalendarDays, ClipboardList,
  BookOpen, FileText, Star, MessageSquare, LogOut, Bell, Library, ClipboardCheck, HelpCircle, FileStack, UsersRound, TrendingUp, Printer, CalendarCheck, Trophy, KeyRound,
} from "lucide-react";

const NAV = [
  { to: "/admin",          label: "Dashboard",    icon: LayoutDashboard },
  { to: "/admin/signups",  label: "Signups",      icon: ClipboardList,  badge: "pending_signups" },
  { to: "/admin/students", label: "Students",     icon: Users },
  { to: "/admin/bookings", label: "Bookings",     icon: CalendarDays,   badge: "pending_bookings" },
  { to: "/admin/questions",label: "Questions",    icon: BookOpen },
  { to: "/admin/bank",     label: "Question Bank",icon: Library },
  { to: "/admin/worksheets", label: "Worksheets",  icon: FileStack },
  // Next to Worksheets because it is built out of them: a programme is an
  // order and a calendar laid over the worksheets above.
  { to: "/admin/programmes", label: "Programmes",  icon: CalendarCheck },
  { to: "/admin/groups",     label: "Groups",      icon: UsersRound },
  { to: "/admin/assignments", label: "Assignments", icon: ClipboardCheck },
  { to: "/admin/print",    label: "Print a test",  icon: Printer },
  // The one place a tutor reads performance. There used to be an Analytics
  // page beside it, counting right answers on a four-option practice quiz
  // that is switched off and that no child ever sat; it could only ever show
  // zeroes. This counts marks against a mark scheme, which is the work.
  { to: "/admin/progress", label: "Progress",     icon: TrendingUp },
  // Beside Progress, and not inside it: Progress asks how well a child is
  // doing, counted in marks. This asks how much of the month they finished,
  // counted in days, and is where the coupon is given out.
  { to: "/admin/star",     label: "Star of the month", icon: Trophy },
  { to: "/admin/requests", label: "Answer Requests", icon: HelpCircle },
  { to: "/admin/blog",     label: "Blog",         icon: FileText },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  { to: "/admin/enquiries",label: "Enquiries",    icon: MessageSquare,  badge: "enquiries" },
];

export default function AdminShell({ children, title, subtitle, stats = {} }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-100 grid md:grid-cols-[260px_1fr]">
      {/* Sidebar */}
      <aside className="bg-[#0A1128] text-white flex flex-col md:min-h-screen">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="text-xl font-bold tracking-tight text-white">Genius Education</div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">Admin Portal</div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, badge }) => {
            const active = loc.pathname === to;
            const badgeCount = badge ? (stats[badge] || 0) : 0;
            return (
              <Link
                key={to}
                to={to}
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active
                    ? "bg-white/10 text-[#D4AF37] font-medium"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                <span className="flex items-center gap-3">
                  <Icon size={16} />
                  {label}
                </span>
                {badgeCount > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-white/10">
          <div className="text-xs text-white/50 mb-3">
            <div className="font-medium text-white/80">{user?.name}</div>
            <div className="capitalize">Tutor · {user?.branch || "Wokingham"}</div>
          </div>
          <Link to="/password"
            className="flex items-center gap-2 text-xs text-white/50 hover:text-white transition-colors mb-2">
            <KeyRound size={13} /> Change my password
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs text-white/50 hover:text-white transition-colors"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">{title}</h1>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            {(stats.pending_signups > 0 || stats.pending_bookings > 0) && (
              <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs px-3 py-1.5 rounded-full">
                <Bell size={12} />
                {(stats.pending_signups || 0) + (stats.pending_bookings || 0)} items need attention
              </div>
            )}
            <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0A1128] text-sm font-bold">
              {user?.name?.[0] || "A"}
            </div>
          </div>
        </header>

        <div className="flex-1 p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
